import { useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import {
    Button,
    Input,
    // Modal,
    // ModalBody,
    // ModalCloseButton,
    // ModalContent,
    // ModalHeader,
    // ModalOverlay,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

import AddIcon from "@/assets/AddIcon";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from "@/common/constants";
import ModalDelete from "@/components/Modals/ModalDelete";
import TableApplicationOptions from "@/components/Tables/TableApplicationOptions";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { deleteApplicationOption, getAllApplicationOptions } from "@/services/applicationOptionService";

import "../styles/ListPage.css";

const ApplicationOptions = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rawDataList, setRawDataList] = useState([]) as any;
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        try {
            const obj = {
                id: idToDelete,
                modifiedBy: name || "System",
            };
            const response = await deleteApplicationOption(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Option deleted.',
                    description: "We have deleted the application option.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Option failed to delete.',
                    description: response?.message || "We failed to delete the option.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Delete error',
                description: "An error occurred while deleting the option.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            onClose();
        }
    };

    const handleSearch = () => {
        setSearchValue(searchInput);
        setCurrentPageNumber(DEFAULT_PAGE_NUMBER);
    };

    const handleEdit = (id: string) => {
        navigate('/settings/application-options/edit', { state: { id } });
    };

    const getData = async () => {
        setIsLoading(true);

        const param = {
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
        };

        try {
            const response = await getAllApplicationOptions(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setRawDataList(response.data || []);
                setTotalPageNumber(response.totalPage || 0);
                setHasNextPage(response.hasNextPage || false);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load application options.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Load error',
                description: "An error occurred while loading application options.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePageNumber = (pageNumber: number) => {
        setCurrentPageNumber(pageNumber);
    };

    const handleChangePageSize = (pageSize: number) => {
        setCurrentPageSize(pageSize);
        setCurrentPageNumber(DEFAULT_PAGE_NUMBER);
    };

    useEffect(() => {
        getData();
    }, [searchValue, currentPageNumber, currentPageSize]);

    return (
        <div style={{ padding: '10px 15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '8px' }}>
                <Input
                    placeholder="Search..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{ width: '250px' }}
                />
                <Button onClick={handleSearch} colorScheme='blue'>
                    Search
                </Button>
                <Button
                    onClick={() => navigate('/settings/application-options/add')}
                    colorScheme='green'
                    leftIcon={<AddIcon />}
                >
                    Add Option
                </Button>
            </div>

            <TableApplicationOptions
                dataList={rawDataList}
                isLoading={isLoading}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                currentPageSize={currentPageSize}
                handleChangePageSize={handleChangePageSize}
                handleChangePageNumber={handleChangePageNumber}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                hasNextPage={hasNextPage}
            />

            <ModalDelete
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={handleConfirmDelete}
                title="Delete Application Option"
                message="Are you sure you want to delete this application option? This action cannot be undone."
            />
        </div>
    );
};

export default ApplicationOptions;
